import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Textarea } from "@/shared/components/ui/textarea";
import { Label } from "@/shared/components/ui/label";
import { useUpdateDocumentType } from "./use-document-types";
import {
  DOCUMENT_TYPE_DESCRIPTION_MAX,
  DOCUMENT_TYPE_NAME_MAX,
  type TosDocumentType,
} from "../shared/compliance-types";
import { resolveComplianceError } from "../shared/resolve-compliance-error";

interface Props {
  readonly target: TosDocumentType | null;
  readonly onOpenChange: (open: boolean) => void;
}

export function EditDocumentTypeDialog({ target, onOpenChange }: Props) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);
  const update = useUpdateDocumentType();

  useEffect(() => {
    if (target) {
      setName(target.name);
      setDescription(target.description ?? "");
      setError(null);
    }
  }, [target]);

  const trimmedName = name.trim();
  const trimmedDescription = description.trim();
  const isDirty =
    target != null &&
    (trimmedName !== target.name || trimmedDescription !== (target.description ?? ""));
  const isValid =
    trimmedName.length > 0 &&
    trimmedName.length <= DOCUMENT_TYPE_NAME_MAX &&
    trimmedDescription.length <= DOCUMENT_TYPE_DESCRIPTION_MAX;

  const handleSubmit = async () => {
    if (!target || !isValid || !isDirty || update.isPending) return;
    setError(null);
    try {
      await update.mutateAsync({
        id: target.id,
        input: {
          name: trimmedName !== target.name ? trimmedName : undefined,
          description:
            trimmedDescription !== (target.description ?? "") ? trimmedDescription : undefined,
        },
      });
      onOpenChange(false);
    } catch (err) {
      setError(resolveComplianceError(err, t));
    }
  };

  return (
    <Dialog open={target != null} onOpenChange={(next) => !update.isPending && onOpenChange(next)}>
      <DialogContent data-testid="compliance-edit-document-type-dialog">
        <DialogHeader>
          <DialogTitle>{t("compliance.documentTypes.edit.title")}</DialogTitle>
          <DialogDescription>
            <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">{target?.key}</code>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="dt-edit-name">{t("compliance.documentTypes.fields.name")}</Label>
            <Input
              id="dt-edit-name"
              data-testid="compliance-edit-document-type-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={DOCUMENT_TYPE_NAME_MAX}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="dt-edit-description">
              {t("compliance.documentTypes.fields.description")}
            </Label>
            <Textarea
              id="dt-edit-description"
              data-testid="compliance-edit-document-type-description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={DOCUMENT_TYPE_DESCRIPTION_MAX}
              rows={3}
            />
          </div>

          {error && (
            <p
              data-testid="compliance-edit-document-type-error"
              className="text-sm text-destructive"
            >
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={update.isPending}>
            {t("compliance.actions.cancel")}
          </Button>
          <Button
            data-testid="compliance-edit-document-type-submit"
            onClick={handleSubmit}
            disabled={!isValid || !isDirty || update.isPending}
          >
            {update.isPending ? t("compliance.actions.saving") : t("compliance.actions.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
